<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Previsao de Vendas',
  },
});

function formatCurrency(value) {
  if (!value) return 'R$ 0';
  if (value >= 1000000) return `R$ ${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `R$ ${(value / 1000).toFixed(1)}K`;
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}

const currentPipeline = computed(() => props.data?.current_pipeline || {});
const forecast = computed(() => props.data?.forecast || {});
const historicalRate = computed(() => props.data?.historical_win_rate || 0);
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-chart-line text-lg text-n-violet-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Current Pipeline Summary -->
      <div class="p-4 rounded-lg bg-n-alpha-1">
        <div class="text-xs font-medium text-n-slate-11 mb-3">
          Pipeline Atual
        </div>
        <div class="grid grid-cols-3 gap-4">
          <div class="text-center">
            <div class="text-lg font-bold text-n-blue-11">
              {{ formatCurrency(currentPipeline.total_value) }}
            </div>
            <div class="text-xs text-n-slate-10">Valor Total</div>
          </div>
          <div class="text-center">
            <div class="text-lg font-bold text-n-violet-11">
              {{ formatCurrency(currentPipeline.weighted_value) }}
            </div>
            <div class="text-xs text-n-slate-10">Ponderado</div>
          </div>
          <div class="text-center">
            <div class="text-lg font-bold text-n-slate-12">
              {{ formatNumber(currentPipeline.deals_count) }}
            </div>
            <div class="text-xs text-n-slate-10">Negocios</div>
          </div>
        </div>
      </div>

      <!-- Historical Win Rate -->
      <div
        class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
      >
        <span class="text-sm text-n-slate-11">Taxa Historica de Ganho</span>
        <div class="flex items-center gap-2">
          <div class="w-24 h-2 bg-n-alpha-2 rounded-full overflow-hidden">
            <div
              class="h-full bg-n-teal-9 rounded-full"
              :style="{ width: `${historicalRate}%` }"
            />
          </div>
          <span class="text-sm font-bold text-n-teal-11">{{ historicalRate.toFixed(1) }}%</span>
        </div>
      </div>

      <!-- Forecast Scenarios -->
      <div class="flex flex-col gap-3">
        <div class="text-xs font-medium text-n-slate-11">
          Cenarios de Previsao (Proximo Trimestre)
        </div>

        <!-- Best Case -->
        <div
          class="flex items-center justify-between p-3 rounded-lg bg-n-teal-2 border border-n-teal-6"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-trending-up text-n-teal-11" />
            <div class="flex flex-col">
              <span class="text-sm font-medium text-n-teal-12">Melhor Cenario</span>
              <span class="text-xs text-n-teal-11">100% dos negocios</span>
            </div>
          </div>
          <span class="text-xl font-bold text-n-teal-11">{{
            formatCurrency(forecast.best_case)
          }}</span>
        </div>

        <!-- Likely Case -->
        <div
          class="flex items-center justify-between p-3 rounded-lg bg-n-blue-2 border border-n-blue-6"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-target text-n-blue-11" />
            <div class="flex flex-col">
              <span class="text-sm font-medium text-n-blue-12">Cenario Provavel</span>
              <span class="text-xs text-n-blue-11">Baseado na probabilidade</span>
            </div>
          </div>
          <span class="text-xl font-bold text-n-blue-11">{{
            formatCurrency(forecast.likely_case)
          }}</span>
        </div>

        <!-- Conservative Case -->
        <div
          class="flex items-center justify-between p-3 rounded-lg bg-n-amber-2 border border-n-amber-6"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-shield text-n-amber-11" />
            <div class="flex flex-col">
              <span class="text-sm font-medium text-n-amber-12">Cenario Conservador</span>
              <span class="text-xs text-n-amber-11">Taxa historica de {{ historicalRate.toFixed(0) }}%</span>
            </div>
          </div>
          <span class="text-xl font-bold text-n-amber-11">{{
            formatCurrency(forecast.conservative_case)
          }}</span>
        </div>
      </div>

      <!-- Monthly Breakdown -->
      <div
        v-if="forecast.monthly_breakdown?.length > 0"
        class="flex flex-col gap-2"
      >
        <div class="text-xs font-medium text-n-slate-11">Previsao Mensal</div>
        <div class="grid grid-cols-3 gap-2">
          <div
            v-for="month in forecast.monthly_breakdown"
            :key="month.month"
            class="p-3 rounded-lg bg-n-alpha-1 text-center"
          >
            <div class="text-xs text-n-slate-10 mb-1">
              {{ month.month_name }}
            </div>
            <div class="text-sm font-bold text-n-violet-11">
              {{ formatCurrency(month.weighted_value) }}
            </div>
            <div class="text-xs text-n-slate-11">
              {{ formatNumber(month.deals_count) }} negocios
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
